import { api } from './api';

// =====================================================
// TIPOS
// =====================================================

export interface Attendant {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AttendantStats extends Attendant {
  total_assignments: number;
  assignments_today: number;
  assignments_this_week: number;
  assignments_this_month: number;
  last_assignment_at: string | null;
}

export interface ConversationAssignment {
  id: string;
  conversation_id: string;
  attendant_id: string;
  assigned_at: string;
  assigned_by?: string;
  notes?: string;
  attendant?: {
    id: string;
    name: string;
    email?: string;
    color: string;
    avatar_url?: string;
  };
}

export interface DashboardData {
  attendants: AttendantStats[];
  totals: {
    total_attendants: number;
    total_assignments: number;
    assignments_today: number;
    assignments_this_week: number;
    assignments_this_month: number;
  };
  filters: {
    date_from: string | null;
    date_to: string | null;
    attendant_id: string | null;
  };
}

export interface AssignmentHistoryItem {
  id: string;
  conversation_id: string;
  attendant_id: string;
  assigned_at: string;
  notes?: string;
  attendant: {
    id: string;
    name: string;
    color: string;
  };
  conversation: {
    id: string;
    contact: {
      id: string;
      name: string;
      phone?: string;
      email?: string;
    };
  };
}

// =====================================================
// CACHE E DEDUPLICAÇÃO DE REQUISIÇÕES
// =====================================================

// Cache client-side para evitar múltiplas chamadas desnecessárias
let _activeAttendantsCache: { data: Attendant[]; timestamp: number } | null = null;
let _allAttendantsCache: { data: Attendant[]; timestamp: number } | null = null;
const CLIENT_CACHE_TTL = 30000; // 30 segundos

// Promises em andamento - garante que N chamadas simultâneas resultem em 1 request HTTP
let _activeAttendantsInflight: Promise<Attendant[]> | null = null;
let _allAttendantsInflight: Promise<Attendant[]> | null = null;

// =====================================================
// CRUD ATENDENTES
// =====================================================

export const attendantsService = {
  /**
   * Listar todos os atendentes.
   * Implementa deduplicação de requisições e cache para evitar flood de chamadas
   * quando múltiplos componentes (<AttendantAssignment>) são renderizados simultaneamente.
   */
  async getAll(activeOnly: boolean = false): Promise<Attendant[]> {
    const cache = activeOnly ? _activeAttendantsCache : _allAttendantsCache;
    const now = Date.now();

    // Retornar do cache se ainda válido
    if (cache && (now - cache.timestamp) < CLIENT_CACHE_TTL) {
      return cache.data;
    }

    // Se já existe uma requisição em andamento, reutilizar a mesma promise
    const inflight = activeOnly ? _activeAttendantsInflight : _allAttendantsInflight;
    if (inflight) {
      return inflight;
    }

    // Criar nova requisição
    const params = activeOnly ? { active_only: 'true' } : {};
    const promise: Promise<Attendant[]> = api.get('/attendants', { params })
      .then((response) => {
        const data: Attendant[] = response.data.data || [];
        const entry = { data, timestamp: Date.now() };
        if (activeOnly) {
          _activeAttendantsCache = entry;
          _activeAttendantsInflight = null;
        } else {
          _allAttendantsCache = entry;
          _allAttendantsInflight = null;
        }
        return data;
      })
      .catch((error) => {
        // Limpar promise em andamento em caso de erro para permitir retry manual
        if (activeOnly) _activeAttendantsInflight = null;
        else _allAttendantsInflight = null;
        throw error;
      });

    if (activeOnly) _activeAttendantsInflight = promise;
    else _allAttendantsInflight = promise;

    return promise;
  },

  /**
   * Invalidar cache manualmente (ex: após criar/editar/excluir atendente)
   */
  invalidateCache() {
    _activeAttendantsCache = null;
    _allAttendantsCache = null;
    _activeAttendantsInflight = null;
    _allAttendantsInflight = null;
  },

  /**
   * Buscar atendente por ID
   */
  async getById(id: string): Promise<Attendant> {
    const response = await api.get(`/attendants/${id}`);
    return response.data.data;
  },

  /**
   * Criar novo atendente
   */
  async create(data: Partial<Attendant>): Promise<Attendant> {
    const response = await api.post('/attendants', data);
    attendantsService.invalidateCache();
    return response.data.data;
  },

  /**
   * Atualizar atendente
   */
  async update(id: string, data: Partial<Attendant>): Promise<Attendant> {
    const response = await api.put(`/attendants/${id}`, data);
    attendantsService.invalidateCache();
    return response.data.data;
  },

  /**
   * Excluir atendente (soft delete por padrão)
   */
  async delete(id: string, permanent: boolean = false): Promise<void> {
    await api.delete(`/attendants/${id}`, {
      params: permanent ? { permanent: 'true' } : {},
    });
    attendantsService.invalidateCache();
  },

  // =====================================================
  // ATRIBUIÇÕES
  // =====================================================

  /**
   * Buscar atribuição de uma conversa
   */
  async getAssignment(conversationId: string): Promise<ConversationAssignment | null> {
    const response = await api.get(`/attendants/assignments/conversation/${conversationId}`);
    return response.data.data;
  },

  /**
   * Atribuir conversa a um atendente
   */
  async assignConversation(
    conversationId: string,
    attendantId: string,
    notes?: string
  ): Promise<ConversationAssignment> {
    const response = await api.post('/attendants/assignments', {
      conversation_id: conversationId,
      attendant_id: attendantId,
      notes,
    });
    return response.data.data;
  },

  /**
   * Remover atribuição de uma conversa
   */
  async removeAssignment(conversationId: string): Promise<void> {
    await api.delete(`/attendants/assignments/${conversationId}`);
  },

  // =====================================================
  // DASHBOARD / ESTATÍSTICAS
  // =====================================================

  /**
   * Buscar dashboard de estatísticas
   */
  async getDashboard(filters?: {
    date_from?: string;
    date_to?: string;
    attendant_id?: string;
  }): Promise<DashboardData> {
    const response = await api.get('/attendants/stats/dashboard', { params: filters });
    return response.data.data;
  },

  /**
   * Buscar histórico de atribuições
   */
  async getHistory(filters?: {
    date_from?: string;
    date_to?: string;
    attendant_id?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: AssignmentHistoryItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const response = await api.get('/attendants/stats/history', { params: filters });
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  },
};

export default attendantsService;
